'use client';

import * as React from 'react';
import Link from 'next/link';
import { BuildingIcon, PlayIcon } from 'lucide-react';
import { motion } from 'motion/react';

import { routes } from '@workspace/routes';
import { Badge } from '@workspace/ui/components/badge';
import { buttonVariants } from '@workspace/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogTitle
} from '@workspace/ui/components/dialog';
import { cn } from '@workspace/ui/lib/utils';

import { GridSection } from '~/components/fragments/grid-section';
import { SiteHeading } from '~/components/fragments/site-heading';

type BedrijvenEnContactpersonenHeroProps = {
  videoId?: string; // YouTube video ID (e.g., "dQw4w9WgXcQ")
};

export function BedrijvenEnContactpersonenHero({
  videoId
}: BedrijvenEnContactpersonenHeroProps): React.JSX.Element {
  const [isVideoOpen, setIsVideoOpen] = React.useState(false);

  return (
    <GridSection>
      <div className="container space-y-12 py-20 md:py-32">
        {/* Badge */}
        <motion.div
          initial={{ filter: 'blur(10px)', opacity: 0, y: -20 }}
          animate={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="flex items-center justify-center"
        >
          <Badge
            variant="outline"
            className="h-8 rounded-full px-3 text-sm font-medium shadow-xs"
          >
            <BuildingIcon className="mr-2 size-4 shrink-0" />
            Bedrijven en Contactpersonen
          </Badge>
        </motion.div>

        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
        >
          <SiteHeading
            title="Al je relaties overzichtelijk en binnen handbereik"
            description="Met Emma beheer je moeiteloos al je bedrijven en contactpersonen. Van de eerste kennismaking tot langdurige samenwerkingen – houd alles bij op één centrale plek. Zo mis je nooit meer een kans en bouw je sterke relaties op."
          />
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.4 }}
          className="mx-auto flex w-full flex-col gap-4 sm:w-auto sm:flex-row sm:justify-center"
        >
          <Link
            href={routes.dashboard.auth.SignUp}
            className={cn(
              buttonVariants({
                variant: 'default'
              }),
              'h-10 rounded-xl sm:h-9'
            )}
          >
            Schrijf je nu in en start direct!
          </Link>
          <Link
            href={routes.marketing.Contact}
            className={cn(
              buttonVariants({
                variant: 'outline'
              }),
              'h-10 rounded-xl sm:h-9'
            )}
          >
            Neem contact op
          </Link>
        </motion.div>

        {/* YouTube Video */}
        {videoId ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.4 }}
            className="mx-auto mt-12 max-w-5xl"
          >
            <button
              type="button"
              onClick={() => setIsVideoOpen(true)}
              className="group relative block aspect-video w-full overflow-hidden rounded-xl border bg-muted transition-shadow hover:shadow-xl"
            >
              <img
                src="/assets/hero/bedrijven.png"
                alt="Video preview"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/20 transition-colors group-hover:bg-black/30">
                <div className="flex size-20 items-center justify-center rounded-full bg-primary shadow-lg">
                  <PlayIcon
                    className="ml-1 size-10 text-white"
                    fill="currentColor"
                  />
                </div>
              </div>
            </button>

            <Dialog
              open={isVideoOpen}
              onOpenChange={setIsVideoOpen}
            >
              <DialogContent className="max-w-5xl p-0">
                <DialogTitle className="sr-only">
                  Bedrijven en Contactpersonen - Emma Demo
                </DialogTitle>
                <div className="relative aspect-video w-full">
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${videoId}`}
                    title="Bedrijven en Contactpersonen - Emma Demo"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              </DialogContent>
            </Dialog>
          </motion.div>
        ) : null}
      </div>
    </GridSection>
  );
}
